import axios from 'axios';
import { addBearerTokenInReq } from './interceptors/addBearerTokenInReq';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { checkIfSessionExpired } from './interceptors/checkIfSessionExpired';
import { AuthService } from './endpoints/AuthService';
import { getLocalAuthStorage } from 'utils/localStorage';
import { localStorageKeys } from 'constants/localStorage';
import { handleAuthError } from './interceptors/handleAuthError';
import { createLoginRetryInterceptor } from './interceptors/loginRetryOn5xx';
import { Mutex } from 'async-mutex';
import { URLS } from 'constants/urls';
import { addSentryLoggingInterceptor } from './interceptors/addSentryLogginInterceptor';
import * as Sentry from '@sentry/react';
const mutex = new Mutex();
export const saveTokensToCookies = (token, refreshToken, expiresIn) => {
  const authExpires = expiresIn ? `; expires=${new Date(expiresIn).toUTCString()}` : '';
  const refreshExpires = `; expires=${new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString()}`;
  document.cookie = `authToken=${token}; path=/; domain=${URLS.cookieDomain}${authExpires}`;
  document.cookie = `refreshToken=${refreshToken}; path=/; domain=${URLS.cookieDomain}${refreshExpires}`;
};
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}
function EnsureResponseSuccess(response) {
  if (response?.status !== 200 || !response?.data?.authToken?.token) {
    const serializedResponse = {
      status: response?.status,
      statusText: response?.statusText,
      data: response?.data,
      headers: response?.headers,
      config: {
        url: response?.config?.url,
        method: response?.config?.method,
      },
    };

    const err = new Error('Resposta inválida da API');
    err.details = JSON.stringify(serializedResponse, null, 2);
    throw err;
  }
  return response;
}

export const refreshAuthLogic = async failedRequest => {
  return mutex.runExclusive(async () => {
    const storage = getLocalAuthStorage();
    const now = Date.now();
    const expires = new Date(storage.expiresIn).getTime();

    if (now < expires) {
      failedRequest.config.headers['Authorization'] = 'Bearer ' + storage.token;

      return Promise.resolve();
    }
    const token = getCookie(`authToken`);
    const refreshToken = getCookie(`refreshToken`);

    if (failedRequest.config.url === '/v1/auth') {
      return Promise.reject(failedRequest.response);
    }

    if (failedRequest.response.status === 401) {
      if (!token || !refreshToken) {
        Sentry.logger.info('[logout] sem token e authtoken');
        window.location.replace(`${process.env.REACT_APP_URL}/logout`);
        return Promise.reject(new Error('Sessão expirada: sem token de autenticação'));
      }
      // Não há necessidade de uma flag `isRefreshing` própria aqui: `mutex.runExclusive`
      // já serializa todas as chamadas a `refreshAuthLogic` — nenhuma outra execução
      // entra neste bloco enquanto esta não terminar. Uma flag redundante existia antes
      // e incluía um branch que se chamava recursivamente por dentro do próprio mutex
      // (não reentrante); removida para eliminar esse risco de deadlock.
      try {
        const response = EnsureResponseSuccess(await AuthService.refreshToken({ token, refreshToken }));

        const newToken = response?.data.authToken.token;
        storage.token = newToken;
        storage.refreshToken = response?.data.authToken.refreshToken;
        storage.expiresIn = response?.data.authToken.expiresIn;
        localStorage.setItem(localStorageKeys.authContext, JSON.stringify(storage));
        saveTokensToCookies(
          response?.data.authToken.token,
          response?.data.authToken.refreshToken,
          response?.data.authToken.expiresIn,
        );
        failedRequest.config.headers['Authorization'] = 'Bearer ' + newToken;
        return Promise.resolve();
      } catch (error) {
        Sentry.logger.info('[logout] erro refresh');
        window.location.replace(`${process.env.REACT_APP_URL}/logout`);

        return Promise.reject(error);
      }
    }

    return Promise.reject(failedRequest);
  });
};

const AxiosMain = axios.create({
  baseURL: process.env.REACT_APP_AUTH_API,
});
const AxiosMainLogin = axios.create({
  baseURL: process.env.REACT_APP_AUTH_API,
});

const AxiosMock = axios.create({
  baseURL: process.env.REACT_APP_MOCK_API,
});

const AxiosCharts = axios.create({
  baseURL: process.env.REACT_APP_LAS_CHART_API,
});

const AxiosGlobalFilters = axios.create({
  baseURL: process.env.REACT_APP_LIGHTHOUSE_FILTERS_API,
});

const AxiosMenuLas = axios.create({
  baseURL: process.env.REACT_APP_LAS,
});
const AxiosMediaCenter = axios.create({
  baseURL: process.env.REACT_APP_MEDIA_CENTER,
});
const AxiosMediaCenterSignature = axios.create({
  baseURL: process.env.REACT_APP_SIGNATURE_IMAGE,
});

// Instância anônima para o Media Center — usada pelo upload de foto do
// formulário sem login. Sem interceptors: o token vem por requisição
// (uploadToken emitido pelo save da ocorrência), não do usuário logado.
// addBearerTokenInReq sobrescreveria esse Authorization se aplicado aqui.
const AxiosMediaCenterPublic = axios.create({
  baseURL: process.env.REACT_APP_MEDIA_CENTER,
});

// Instância anônima para os endpoints "públicos" do chamado sem login
// (formulário acessado via QR code). Sem interceptors de auth: essas
// chamadas não exigem sessão, mas um authContext expirado sobrando no
// localStorage de um dispositivo que já teve login fazia checkIfSessionExpired
// disparar refreshAuthLogic e redirecionar para /logout mesmo sem necessidade.
const AxiosGlobalFiltersPublic = axios.create({
  baseURL: process.env.REACT_APP_LIGHTHOUSE_FILTERS_API,
});

// --- Request interceptors ---
// 1️ Adiciona token
// 2️Verifica se sessão está expirada localmente e faz refresh se necessário
AxiosMediaCenter.interceptors.request.use(addBearerTokenInReq);
AxiosMediaCenter.interceptors.request.use(checkIfSessionExpired);

AxiosMediaCenterSignature.interceptors.request.use(addBearerTokenInReq);
AxiosMediaCenterSignature.interceptors.request.use(checkIfSessionExpired);

AxiosGlobalFilters.interceptors.request.use(addBearerTokenInReq);
AxiosGlobalFilters.interceptors.request.use(checkIfSessionExpired);

AxiosCharts.interceptors.request.use(addBearerTokenInReq);
AxiosCharts.interceptors.request.use(checkIfSessionExpired);

AxiosMenuLas.interceptors.request.use(addBearerTokenInReq);
AxiosMenuLas.interceptors.request.use(checkIfSessionExpired);

AxiosMain.interceptors.request.use(addBearerTokenInReq);
AxiosMain.interceptors.request.use(checkIfSessionExpired);

AxiosMainLogin.interceptors.request.use(addBearerTokenInReq);
AxiosMainLogin.interceptors.request.use(checkIfSessionExpired);

// --- Response interceptors ---
// Retry automático para erros 5xx
AxiosMain.interceptors.response.use(res => res, createLoginRetryInterceptor(AxiosMain, handleAuthError));
AxiosMainLogin.interceptors.response.use(res => res, createLoginRetryInterceptor(AxiosMainLogin, handleAuthError));

//  Refresh reativo em caso de 401 retornado do backend
createAuthRefreshInterceptor(AxiosGlobalFilters, refreshAuthLogic);
createAuthRefreshInterceptor(AxiosMediaCenterSignature, refreshAuthLogic);
createAuthRefreshInterceptor(AxiosMediaCenter, refreshAuthLogic);
createAuthRefreshInterceptor(AxiosMenuLas, refreshAuthLogic);
createAuthRefreshInterceptor(AxiosCharts, refreshAuthLogic);

// 5️Logging no Sentry
if (process.env.REACT_APP_FEATURE_NEW === 'true') {
  addSentryLoggingInterceptor(AxiosMain);
  addSentryLoggingInterceptor(AxiosMainLogin);
  addSentryLoggingInterceptor(AxiosCharts);
  addSentryLoggingInterceptor(AxiosGlobalFilters);
  addSentryLoggingInterceptor(AxiosMenuLas);
  addSentryLoggingInterceptor(AxiosMediaCenter);
  addSentryLoggingInterceptor(AxiosMediaCenterSignature);
  addSentryLoggingInterceptor(AxiosMock);
}
export {
  AxiosMain,
  AxiosMainLogin,
  AxiosCharts,
  AxiosGlobalFilters,
  AxiosMock,
  AxiosMenuLas,
  AxiosMediaCenter,
  AxiosMediaCenterSignature,
  AxiosMediaCenterPublic,
  AxiosGlobalFiltersPublic,
};
